import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  Newspaper,
  FileText,
  Image,
  Building2
} from 'lucide-react';

const Press = () => {
  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SEO
        title="Press — Engaged Manila Media & Press Kit"
        description="Press resources, media kit, and company information for Engaged Manila, the influencer marketing and product review platform."
        url="/press"
      />
      <Header />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/90 to-blue-800 pt-28 pb-20 overflow-hidden">
        {/* Abstract shapes background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Large circle */}
          <svg className="absolute -right-20 -top-20 w-96 h-96 text-white/5" viewBox="0 0 400 400" fill="none">
            <motion.circle 
              cx="200" 
              cy="200" 
              r="200" 
              fill="currentColor"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
          
          {/* Small circles pattern */}
          <svg className="absolute left-1/4 bottom-0 w-64 h-64 text-white/5" viewBox="0 0 200 200" fill="none">
            <motion.circle cx="50" cy="50" r="10" fill="currentColor" 
              animate={{ opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle cx="150" cy="30" r="8" fill="currentColor" 
              animate={{ opacity: [0.5, 0.2, 0.5] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle cx="100" cy="100" r="15" fill="currentColor" 
              animate={{ opacity: [0.2, 0.6, 0.2] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle cx="30" cy="130" r="7" fill="currentColor" 
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle cx="160" cy="160" r="12" fill="currentColor" 
              animate={{ opacity: [0.6, 0.3, 0.6] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0" 
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3Ccircle cx='13' cy='13' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '20px 20px'
            }}
          />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full bg-blue-400/10 backdrop-blur-sm border border-blue-300/20">
              <span className="text-sm font-medium text-blue-100">Engaged Manila</span>
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              <span className="block">Press & Media Center</span>
            </h1>
            <p className="mt-6 text-xl leading-8 text-blue-100">
              Official news, announcements, media kits, and resources for journalists and media professionals
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="py-24 bg-gray-50 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Coming Soon Section */}
          <motion.div 
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center mb-8 h-16 w-16 rounded-full bg-primary/10 ring-1 ring-primary/20">
                <Newspaper className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                Media Resources Coming Soon
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-600 max-w-2xl mx-auto">
                We're preparing comprehensive media resources to help journalists and media professionals cover Engaged Manila's story and impact on the influencer marketing industry.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="mailto:press@engagedmanila.com"
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all duration-200"
                >
                  Contact Press Team
                </a>
                <Link to="/about-us" className="text-sm font-semibold leading-6 text-gray-900 flex items-center">
                  About Us
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* Press Resources */}
          <div className="mt-32">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">Future Press Resources</h2>
              <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                Our media center will provide everything you need to cover Engaged Manila accurately and effectively
              </p>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-1 gap-y-12 gap-x-8 sm:grid-cols-2 lg:grid-cols-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <FileText className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Press Releases</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    Access our archive of press releases covering company milestones, product launches, and major announcements.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <Image className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Media Kit</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    Download high-resolution logos, executive headshots, product screenshots, and brand guidelines for publication.
                  </p>
                </div>
              </div>
              
              <div className="relative group">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 rounded-2xl bg-gray-50 group-hover:bg-gray-100/80 transition duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold leading-8 tracking-tight text-gray-900">Company Information</h3>
                  <p className="mt-3 text-base leading-7 text-gray-600">
                    Find key facts about Engaged Manila, our mission, leadership team, and impact on the influencer marketing ecosystem.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Media Contact Section */}
          <motion.div 
            className="mt-32 relative isolate overflow-hidden bg-white rounded-3xl shadow-sm border border-gray-200 py-16 sm:py-24 lg:py-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <svg
                className="absolute left-[max(50%,25rem)] top-0 h-[64rem] w-[128rem] -translate-x-1/2 stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)]"
                aria-hidden="true"
              >
                <defs>
                  <pattern
                    id="e813992c-7d03-4cc4-a2bd-151760b470a0"
                    width="200"
                    height="200"
                    x="50%"
                    y="-1"
                    patternUnits="userSpaceOnUse"
                  >
                    <path d="M100 200V.5M.5 .5H200" fill="none" />
                  </pattern>
                </defs>
                <svg x="50%" y="-1" className="overflow-visible fill-gray-50">
                  <path
                    d="M-100.5 0h201v201h-201Z M699.5 0h201v201h-201Z M499.5 400h201v201h-201Z M-300.5 600h201v201h-201Z"
                    strokeWidth="0"
                  />
                </svg>
                <rect width="100%" height="100%" strokeWidth="0" fill="url(#e813992c-7d03-4cc4-a2bd-151760b470a0)" />
              </svg>
            </div>
            
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Media Inquiries</h2>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                Our communications team is available to assist journalists, bloggers, and media professionals with inquiries, interviews, and information requests.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="mailto:press@engagedmanila.com"
                  className="rounded-md bg-primary px-5 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all duration-200"
                >
                  <span className="flex items-center">
                    Contact Press Team
                  </span>
                </a>
              </div>
              <p className="mt-6 text-sm text-gray-500">
                Email: press@engagedmanila.com
              </p>
            </div>
          </motion.div>
          
          {/* Return to Home */}
          <div className="mt-16 text-center">
            <Link 
              to="/"
              className="inline-flex items-center text-primary hover:text-primary-dark font-medium transition-colors duration-200"
            >
              <span>Return to Home</span>
            </Link>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Press;
